import { Component, Input, forwardRef, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';

export interface ImageFieldConfig {
  multiple?: boolean;
  maxFiles?: number;
  previewType?: 'grid' | 'list' | 'carousel';
  previewSize?: 'small' | 'medium' | 'large';
  showFileSize?: boolean;
  showRemoveButton?: boolean;
  acceptTypes?: string[];
  maxFileSize?: number; // en MB
  dragAndDrop?: boolean;
  autoPreview?: boolean;
}

@Component({
  selector: 'app-image-field',
  templateUrl: './image-field.component.html',
  styleUrls: ['./image-field.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageFieldComponent),
      multi: true
    }
  ],
  animations: [
    trigger('fadeInOut', [
      state('void', style({
        opacity: 0,
        transform: 'translateY(-10px)'
      })),
      state('*', style({
        opacity: 1,
        transform: 'translateY(0)'
      })),
      transition('void <=> *', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class ImageFieldComponent implements ControlValueAccessor {
  @Input() submitted: boolean = false;
  @Input() config: ImageFieldConfig = {
    multiple: false,
    maxFiles: 1,
    previewType: 'grid',
    previewSize: 'medium',
    showFileSize: true,
    showRemoveButton: true,
    acceptTypes: ['image/*'],
    maxFileSize: 10,
    dragAndDrop: false,
    autoPreview: true
  };

  selectedFiles: File[] = [];
  imagePreviews: string[] = [];
  dragOver = false;

  imageForm: FormGroup = this.fb.group({
    images: [null] // Image is not required
  });

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.imageForm.valueChanges.subscribe(value => {
      this.onChange(value);
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.imageForm.patchValue(value, { emitEvent: false });
      if (value.images) {
        // Nettoyer les aperçus existants avant de charger de nouvelles images
        this.imagePreviews = [];
        
        if (Array.isArray(value.images)) {
          this.selectedFiles = value.images;
          this.createImagePreviews(value.images);
        } else {
          this.selectedFiles = [value.images];
          this.createImagePreviews([value.images]);
        }
      }
    } else {
      // Si aucune valeur, nettoyer tout
      this.selectedFiles = [];
      this.imagePreviews = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.imageForm.disable();
    } else {
      this.imageForm.enable();
    }
  }

  get imageControl() {
    return this.imageForm.get('images');
  }

  onFileSelected(event: any) {
    const files = Array.from(event.target.files) as File[];
    this.processFiles(files);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
    
    if (event.dataTransfer?.files) {
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    }
  }

  private processFiles(files: File[]) {
    const validFiles = files.filter(file => this.validateFile(file));
    
    if (this.config.multiple) {
      const totalFiles = this.selectedFiles.length + validFiles.length;
      if (totalFiles > this.config.maxFiles!) {
        this.showError(`Maximum ${this.config.maxFiles} files allowed`);
        return;
      }
      this.selectedFiles.push(...validFiles);
    } else {
      // Nettoyer les aperçus existants avant de sélectionner de nouveaux fichiers
      this.imagePreviews = [];
      this.selectedFiles = validFiles.slice(0, 1);
    }

    this.createImagePreviews(validFiles);
    this.updateFormValue();
    this.onTouched();
  }

  private validateFile(file: File): boolean {
    // Vérifier le type de fichier
    if (this.config.acceptTypes && this.config.acceptTypes.length > 0) {
      const isValidType = this.config.acceptTypes.some(type => {
        if (type === 'image/*') {
          return file.type.startsWith('image/');
        }
        return file.type === type;
      });
      if (!isValidType) {
        this.showError(`Invalid file type: ${file.name}`);
        return false;
      }
    }

    // Vérifier la taille du fichier
    if (this.config.maxFileSize) {
      const maxSizeBytes = this.config.maxFileSize * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        this.showError(`File too large: ${file.name} (max ${this.config.maxFileSize}MB)`);
        return false;
      }
    }

    return true;
  }

  private showError(message: string) {
    // Ici vous pouvez implémenter un système de notification
    console.error(message);
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);
    this.updateFormValue();
    this.onTouched();
    this.cdr.detectChanges();
  }

  removeAllFiles() {
    this.selectedFiles = [];
    this.imagePreviews = [];
    this.updateFormValue();
    this.onTouched();
    this.cdr.detectChanges();
  }

  private updateFormValue() {
    const value = this.config.multiple ? this.selectedFiles : this.selectedFiles[0] || null;
    this.imageForm.patchValue({ images: value });
  }

  getFileSize(file: File): string {
    const sizeInBytes = file.size;
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  getPreviewClass(): string {
    const sizeClass = this.config.previewSize || 'medium';
    const typeClass = this.config.previewType || 'grid';
    return `preview-${typeClass} preview-${sizeClass}`;
  }

  private createImagePreviews(files: File[]) {
    if (!this.config.autoPreview) return;

    // Nettoyer les aperçus existants si on n'est pas en mode multiple
    if (!this.config.multiple) {
      this.imagePreviews = [];
    }

    files.forEach(file => {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.imagePreviews.push(e.target.result);
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
      }
    });
  }

  get selectedFilesCount(): number {
    return this.selectedFiles.length;
  }

  get canAddMore(): boolean {
    if (!this.config.multiple) return false;
    return this.selectedFiles.length < this.config.maxFiles!;
  }
}
